// **The Fullscreen seam** (`demo-presentation-shell` design, requirement "Fullscreen is entered
// from the first presenter interaction"). Structurally identical to `test/fake-fullscreen.ts`'s
// `FullscreenApi` (that double was built first, against this shape, per that file's own header) —
// kept as its own tiny declaration here rather than importing across the test/production boundary,
// so production code never depends on `src/test/`.

export interface FullscreenApi {
  /** Requests fullscreen for `element`. Resolves once granted; rejects if refused. */
  request(element: unknown): Promise<void>;
  /** Whether *something* is currently fullscreen. */
  isFullscreen(): boolean;
  /** Whether the API exists on this browser/device at all. */
  readonly available: boolean;
}

/** The real browser implementation — this prop's production default. Absent entirely on a
 * browser/device with no Fullscreen API (checked via `document.fullscreenEnabled`, itself absent
 * on some engines, hence the `in` guard). */
export function createBrowserFullscreenApi(): FullscreenApi {
  return {
    get available() {
      return typeof document !== 'undefined' && 'fullscreenEnabled' in document
        ? Boolean((document as unknown as { fullscreenEnabled?: boolean }).fullscreenEnabled)
        : false;
    },
    request(element: unknown): Promise<void> {
      const target = element as { requestFullscreen?: () => Promise<void> } | null;
      if (target?.requestFullscreen === undefined) {
        return Promise.reject(new Error('the Fullscreen API is not available on this element'));
      }
      return target.requestFullscreen();
    },
    isFullscreen(): boolean {
      return typeof document !== 'undefined' && document.fullscreenElement != null;
    },
  };
}
